import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import type {
  ImportValidationIssue,
  LogoKind,
  NewEntity,
  NewGroupEntity,
  NewSourceEntity,
} from "../api/types";
import { useAddGroup, useAddSource } from "../api/lists";
import { EntityFormDialog } from "./Settings/EntityFormDialog";

interface ImportErrorModalProps {
  issues: ImportValidationIssue[];
  onClose: () => void;
  onRetry?: () => void;
}

type IssueDescriptor = {
  label: string;
  name: string;
  entityType: LogoKind;
  title: string;
};

const describeIssue = (issue: ImportValidationIssue): IssueDescriptor => {
  switch (issue.type) {
    case "sourceNotFound":
      return {
        label: `Source not found: ${issue.source}`,
        name: issue.source,
        entityType: "source",
        title: "Source",
      };
    case "groupNotFound":
      return {
        label: `Group not found: ${issue.group}`,
        name: issue.group,
        entityType: "group",
        title: "Group",
      };
  }
};

export const ImportErrorModal = ({
  issues,
  onClose,
  onRetry,
}: ImportErrorModalProps) => {
  const [resolved, setResolved] = useState<Set<number>>(new Set());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  const addSource = useAddSource();
  const addGroup = useAddGroup();

  const editingIssue = editingIndex !== null ? issues[editingIndex] : undefined;
  const editingDescriptor = editingIssue
    ? describeIssue(editingIssue)
    : undefined;

  const allResolved = issues.length > 0 && resolved.size === issues.length;

  const handleSave = async (entity: NewEntity<string>) => {
    if (editingIndex === null || !editingIssue) return;
    setSaveError(undefined);
    try {
      switch (editingIssue.type) {
        case "sourceNotFound":
          await addSource.mutateAsync(entity as NewSourceEntity);
          break;
        case "groupNotFound":
          await addGroup.mutateAsync(entity as NewGroupEntity);
          break;
      }
      setResolved((prev) => {
        const next = new Set(prev);
        next.add(editingIndex);
        return next;
      });
      setEditingIndex(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <Dialog
        open={issues.length > 0}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Validation Errors</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            The following issues prevented the import. Add the missing entries
            and retry.
          </Typography>
          <List dense>
            {issues.map((issue, index) => {
              const descriptor = describeIssue(issue);
              const isResolved = resolved.has(index);
              return (
                <ListItem
                  key={index}
                  secondaryAction={
                    isResolved ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "success.main",
                          pr: 1,
                        }}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setSaveError(undefined);
                          setEditingIndex(index);
                        }}
                      >
                        Add
                      </Button>
                    )
                  }
                >
                  <ListItemText
                    primary={descriptor.label}
                    slotProps={{
                      primary: {
                        sx: isResolved
                          ? { textDecoration: "line-through", opacity: 0.6 }
                          : undefined,
                      },
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {onRetry && (
            <Button
              variant="contained"
              color="primary"
              disabled={!allResolved}
              onClick={onRetry}
            >
              Retry import
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {editingDescriptor && (
        <EntityFormDialog
          open
          title={editingDescriptor.title}
          entityType={editingDescriptor.entityType}
          defaultName={editingDescriptor.name}
          error={saveError}
          onClose={() => {
            setEditingIndex(null);
            setSaveError(undefined);
          }}
          onSave={handleSave}
        />
      )}
    </>
  );
};
